import { props, map, pathOr } from 'ramda'
import URLSearchParams from 'url-search-params'

import {
  BODY_NO_SCROLL_CLASS,
  BODY_NO_SCROLL_MOBILE_CLASS,
  BUTTON_OFF,
  BUTTON_ON,
  SEARCH_FILTERS_FORM,
  SEARCH_PARAM_DATE_END,
  SEARCH_PARAM_DATE_START,
  SEARCH_PARAM_APPARTMENT,
  SEARCH_PARAM_CLIENT_CODE,
  SEARCH_PARAM_IATA_CODE,
  SEARCH_PARAM_PROMO_CODE,
} from '../constants'
import {
  ICON_BUSINESS,
  ICON_DISABLED_PEOPLE,
  ICON_FAMILY,
  ICON_MEETING_ROOM,
  ICON_PARKING,
  ICON_PETS_ALLOWED,
  ICON_POOL,
  ICON_SPORTS_HALL,
  ICON_AIR_CONDITIONER,
  PRICE_HIGH,
  PRICE_LOW,
  PRICE_MIDDLE,
} from '../components/Filters/More/constants'
import {generate} from "shortid";

const oneDayBooking = pathOr(null, ['drupalSettings', 'oneDayBooking'], window)

const formatMonth = (date, locale) => {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'short' })
  const month = formatter.format(date).toLocaleLowerCase()
  return month[month.length - 1] !== '.' ? `${month}.` : month
}

/**
 * Selected period for Dates Filter
 *
 * @param dates<Array>
 * @param fromLabel<String>
 * @param toLabel<String>
 * @param locale<String>
 * @returns any
 */
export const selectedPeriod = (dates, fromLabel, toLabel, locale) => {
  let from = ''
  let to = ''

  if (Array.isArray(dates)) {
    const dateFrom = new Date(dates[0])
    from = `${dateFrom.toLocaleDateString(locale, { weekday: 'short' })}, ${dateFrom.getDate()} ${formatMonth(dateFrom, locale)}`

    if (dates.length === 2 && dates[1]) {
      const dateTo = new Date(dates[1])
      to = `${dateTo.toLocaleDateString(locale, { weekday: 'short' })}, ${dateTo.getDate()} ${formatMonth(dateTo, locale)}`
    }
  }

  const rangeBookingMarkup = `
      <span class="period-wrap">
        <span class="period-label">${fromLabel}</span>
        <span class="period start-day">${from}</span>
      </span>
      <span class="period-divider"></span>
      <span class="period-wrap">
        <span class="period-label">${toLabel}</span>
        <span class="period end-day">${to}</span>
      </span>
    `

  const oneDayBookingMarkup = `<span class="period start-day one-day-booking">${from}</span>`

  return oneDayBooking ? oneDayBookingMarkup : rangeBookingMarkup
}

export const filledRooms = rooms => (
  rooms
    ? rooms.filter(room => (
      (room.adults + room.children + room.babies) > 0 && room.visible === true
    ))
    : []
)

export const countRooms = rooms => (
  filledRooms(rooms).length
)

const countGuests = (rooms, guest) => {
  let guests = 0
  filledRooms(rooms)
    .filter(room => room[guest] > 0)
    .forEach((room) => { guests += room[guest] })
  return guests
}

export const selectedRooms = (
  roomsArray,
  {
    adult,
    adults,
    child,
    children,
    baby,
    babies,
    room,
    rooms,
  },
  defaultLabel,
) => {
  const roomsLabel = []
  const roomsCount = countRooms(roomsArray)
  if (roomsCount > 0) {
    roomsLabel.push(`${roomsCount} ${roomsCount > 1 ? rooms : room}`)

    const adultsCount = countGuests(roomsArray, 'adults')
    if (adultsCount > 0) {
      roomsLabel.push(`${adultsCount} ${adultsCount > 1 ? adults : adult}`)
    }

    const childrenCount = countGuests(roomsArray, 'children')
    if (childrenCount > 0) {
      roomsLabel.push(`${childrenCount} ${childrenCount > 1 ? children : child}`)
    }

    const babiesCount = countGuests(roomsArray, 'babies')
    if (babiesCount > 0) {
      roomsLabel.push(`${babiesCount} ${babiesCount > 1 ? babies : baby}`)
    }


  }
  return roomsLabel.length > 0 ? roomsLabel.join(', ') : defaultLabel
}

export const getActivePrices = prices => (
  prices
    ? Object.entries(prices).filter(value => value[1] === BUTTON_ON).map(id => id)
    : []
)

export const getActiveAmenities = amenities => (
  amenities
    ? Object.entries(amenities).filter(value => value[1] === BUTTON_ON).map(id => id)
    : []
)

export const getActiveHotelType = type => type || BUTTON_OFF

const getPriceTitle = (filedName, titles) => {
  let title
  switch (filedName) {
    case PRICE_LOW:
      title = titles.lowPrice
      break
    case PRICE_MIDDLE:
      title = titles.middlePrice
      break
    case PRICE_HIGH:
      title = titles.highPrice
      break
    default:
  }
  return title
}

const getAmenityTitle = (id, titles) => {
  let title
  switch (id) {
    case ICON_DISABLED_PEOPLE:
      title = titles.disabledPeople
      break
    case ICON_PETS_ALLOWED:
      title = titles.petsAllowed
      break
    case ICON_PARKING:
      title = titles.parking
      break
    case ICON_POOL:
      title = titles.pool
      break
    case ICON_SPORTS_HALL:
      title = titles.sportsHall
      break
    case ICON_MEETING_ROOM:
      title = titles.meetingRoom
      break
    case ICON_AIR_CONDITIONER:
      title = titles.airConditioner
      break
    default:
  }
  return title
}

const getHotelTypeTitle = (id, titles) => {
  let title
  switch (id) {
    case ICON_BUSINESS:
      title = titles.business
      break
    case ICON_FAMILY:
      title = titles.family
      break
    default:
  }
  return title
}

export const selectedMore = (
  { priceRange, amenities, hotelType },
  titles,
  defaultLabel,
) => {
  const label = []

  const activePrices = getActivePrices(priceRange)
  const activeAmenities = getActiveAmenities(amenities)
  const activeHotelType = getActiveHotelType(hotelType)

  if (activePrices.length) {
    activePrices.forEach(item => label.push(getPriceTitle(item[0], titles.priceRange)))
  }

  if (activeAmenities.length) {
    activeAmenities.forEach(item => label.push(getAmenityTitle(item[0], titles.amenities.icons)))
  }

  if (activeHotelType && activeHotelType !== BUTTON_OFF) {
    label.push(getHotelTypeTitle(activeHotelType, titles.hotelType.icons))
  }

  return label.length > 0 ? label.join(' - ') : defaultLabel
}

export const selectedCode = (
  titles,
  defaultLabel,
) => {
  const label = []

  return label.length > 0 ? label.join(' - ') : defaultLabel
}

export const isMoreFiltersFieldsFilled = more => (
  getActivePrices(more.priceRange).length > 0
  || getActiveAmenities(more.amenities).length > 0
  || getActiveHotelType(more.hotelType) !== BUTTON_OFF
)

export const isCodeFiltersFieldFilled = values => (
  !!values && !!props(
    [SEARCH_PARAM_CLIENT_CODE,
      SEARCH_PARAM_IATA_CODE,
      SEARCH_PARAM_PROMO_CODE,
    ], map(item => (typeof item === 'string' ? item.trim() : item), values),
  ).find(value => !!value)
)

const formatUrlDate = date => `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`

export const URLDateParam = UTCDate => formatUrlDate(new Date(UTCDate))

export const dateFromURLParam = date => (
  new Date(date)
)

export const dateEndFromNights = (dateIn, nbNight) => {
  const dateStart = dateFromURLParam(dateIn)
  const nights = parseInt(nbNight, 10)
  dateStart.setDate(dateStart.getDate() + nights)
  return formatUrlDate(dateStart)
}

export const nightsFromPeriod = (period) => {
  const oneDay = (1000 * 60 * 60 * 24)
  if (period && period.length === 2) {
    const start = URLDateParam(period[0])
    const end = URLDateParam(period[1])
    const nightsNumber = ((new Date(end)).getTime() - (new Date(start)).getTime()) / oneDay

    return !oneDayBooking
      ? nightsNumber
      : nightsNumber > 0
        ? nightsNumber
        : 1
  }
  return 0
}

export const selectedSort = (item, titles) => (
  (item <= titles.length && item > 0) ? titles[item - 1] : ''
)

const bodyScroll = (hide = true, mobilesOnly) => {
  const bodyClassList = document.querySelector('body').classList
  const className = mobilesOnly ? BODY_NO_SCROLL_MOBILE_CLASS : BODY_NO_SCROLL_CLASS
  if (hide) {
    bodyClassList.add(className)
  } else {
    bodyClassList.remove(className)
  }
  return true
}

export const hideBodyScroll = (mobilesOnly = false) => bodyScroll(true, mobilesOnly)

export const showBodyScroll = (mobilesOnly = false) => bodyScroll(false, mobilesOnly)


export const getFiltersData = (state) => {
  const data = {}
  const {
    form: {
      [SEARCH_FILTERS_FORM]: {
        dates,
        datesApply,
        rooms,
        roomsApply,
      },
    },
  } = state
  
  if (dates && datesApply) {
    data[SEARCH_PARAM_DATE_START] = URLDateParam(dates[0])
    data[SEARCH_PARAM_DATE_END] = nightsFromPeriod(dates)
  }

  if (rooms && roomsApply) {
    const filledRoomsData = filledRooms(rooms)
      .map(r => ({
        adults: r.adults,
        children: r.children,
        babies: r.babies,
      }))

    const roomsArray = []

    filledRoomsData
      .forEach((room) => {
        roomsArray.push(`${Object.values(room).join(',')}`)
      })

    data[SEARCH_PARAM_APPARTMENT] = roomsArray
  }

  return data
}

export const updateDatesInSearchString = (link, dates) => {
  const [url, params] = link.split('?')
  const searchParams = new URLSearchParams(params)

  if (dates.length === 2) {
    searchParams.set('dateIn', URLDateParam(dates[0]))
    searchParams.set('nbNight', nightsFromPeriod(dates))
  }

  return [url, searchParams].join('?')
}

export const updateLocationInSearchString = (link, location) => {
  const [url, params] = link.split('?')
  const searchParams = new URLSearchParams(params)

  if (dates.length === 2) {
    searchParams.set('dateIn', URLDateParam(dates[0]))
    searchParams.set('nbNight', nightsFromPeriod(dates))
  }

  return [url, searchParams].join('?')
}


export const scrollToFilters = (el, offset) => {
  const searchInHeader = !!document.querySelector('header #search-box')
  if(!searchInHeader) {
    const scrollToEl = document.querySelector(el)
    const ttl = document.querySelector('.search-home__title')
    const sttl = document.querySelector('.search-home__text')
    const tileHeight = ttl ? ttl.offsetHeight : 0
    const subtitleHeight = sttl ? sttl.offsetHeight : 0
    const scrollToElTop = scrollToEl.offsetTop + tileHeight + subtitleHeight - 290 - offset
    // eslint-disable-next-line max-len
    const windowTop = (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0)
    if (windowTop < 500) {
      window.scrollTo(0, scrollToElTop)
    }
  }
}

export const getFilledRooms = (state) => {
  const roomsArray = []
  const {
    [SEARCH_FILTERS_FORM]: {
      rooms,
    },
  } = state
  if (rooms) {
    const filledRoomsData = filledRooms(rooms)
        .map(r => ({
          adults: r.adults,
          children: r.children,
          babies: r.babies,
          id: r.id || generate(),
        }))
    filledRoomsData
        .forEach((room) => {
          roomsArray.push(`${Object.values(room).join(',')}`)
        })
  }

  return roomsArray
}


export const updateDatesInSearchStringBooking = (link, dates, rooms) => {
  const [url, params] = link.split('?')
  const searchParams = new URLSearchParams(params)
  searchParams.delete(SEARCH_PARAM_APPARTMENT)

  if (dates.length === 2) {
    searchParams.set('dateIn', URLDateParam(dates[0]))
    searchParams.set('nbNight', nightsFromPeriod(dates))
  }
  // update rooms
  rooms.forEach((room, i) => {
    if (i === 0) searchParams.set('apartment[]', room)
    else searchParams.append('apartment[]', room)
  })

  return [url, searchParams].join('?')
}

export const findHotelByCode = (list, codeToFind) => {
  for (const city of list) {
    for (const hotelCode in city.hotels) {
      if (Number(city.hotels[hotelCode].code) === Number(codeToFind)) {
        return true
      }
    }
  }
  return false
}
